// Drawing text and image (photo / logo slot) objects on Canvas 2D (M14). Used by the tiles (preview) and by
// the PNG export through paintItem(), so both are the same pixels.
import type { ColorRef, Doc, ImageNode, Subpath, TextNode } from '../model/types';
import { shapeToSubpaths } from '../geometry/bezier';
import { getBitmap } from '../assets';
import { layoutText } from '../text/layout';
import { composite, cssColor, paintStyle, applyStroke } from './paint';

/** The frame of an image slot in its own coordinates (from 0, 0). */
export function framePath(n: ImageNode): Subpath[] {
  const { w, h, shape, radius } = n.frame;
  if (shape === 'ellipse')
    return shapeToSubpaths({ type: 'ellipse', cx: w / 2, cy: h / 2, rx: w / 2, ry: h / 2 });
  const r = shape === 'rounded' ? Math.min(radius, w / 2, h / 2) : 0;
  return shapeToSubpaths({ type: 'rect', x: 0, y: 0, w, h, radii: [r, r, r, r] });
}

/** Where the image is drawn inside its frame (local coordinates): cover / contain × zoom, plus the offset. */
export function imageDrawRect(n: ImageNode): { x: number; y: number; w: number; h: number } {
  const { w, h } = n.frame;
  if (!n.iw || !n.ih) return { x: 0, y: 0, w, h };
  if (n.slice) {
    // M21b: the slice (x0..x1, y0..y1 of the image) fitted into the frame, against the side it was cut on.
    const { x0, x1, y0, y1 } = n.slice;
    const sw = n.iw * (x1 - x0),
      sh = n.ih * (y1 - y0);
    const s = Math.min(w / sw, h / sh) * Math.max(0.1, n.zoom);
    const dw = n.iw * s,
      dh = n.ih * s;
    const pw = sw * s,
      ph = sh * s;
    const left = x1 < 1 && x0 === 0 ? w - pw : x0 > 0 && x1 === 1 ? 0 : (w - pw) / 2;
    const top = (h - ph) / 2;
    return {
      x: left - x0 * dw + n.offsetX * w,
      y: top - y0 * dh + n.offsetY * h,
      w: dw,
      h: dh,
    };
  }
  const s =
    (n.fit === 'cover' ? Math.max(w / n.iw, h / n.ih) : Math.min(w / n.iw, h / n.ih)) * Math.max(0.1, n.zoom);
  const dw = n.iw * s,
    dh = n.ih * s;
  return { x: (w - dw) / 2 + n.offsetX * w, y: (h - dh) / 2 + n.offsetY * h, w: dw, h: dh };
}

/** Text: the glyph outlines filled with the text colour (object opacity applies). */
export function drawText(g: CanvasRenderingContext2D, n: TextNode, path: Path2D | null, doc: Doc | null) {
  if (!path || !layoutText(n)?.commands.length) return;
  g.fillStyle = cssColor(n.style.color, doc);
  g.fill(path, 'nonzero');
}

/** An empty photo slot: light grey with a simple picture sign (no text, so it needs no font). */
function placeholder(g: CanvasRenderingContext2D, n: ImageNode) {
  const { w, h } = n.frame;
  g.fillStyle = 'rgba(205,205,205,1)';
  g.fillRect(0, 0, w, h);
  const s = Math.min(w, h) * 0.22;
  const cx = w / 2,
    cy = h / 2;
  g.fillStyle = 'rgba(160,160,160,1)';
  g.beginPath();
  g.moveTo(cx - s, cy + s * 0.6);
  g.lineTo(cx - s * 0.3, cy - s * 0.3);
  g.lineTo(cx + s * 0.1, cy + s * 0.2);
  g.lineTo(cx + s * 0.45, cy - s * 0.1);
  g.lineTo(cx + s, cy + s * 0.6);
  g.closePath();
  g.fill();
  g.beginPath();
  g.arc(cx + s * 0.55, cy - s * 0.55, s * 0.2, 0, Math.PI * 2);
  g.fill();
}

/**
 * M21: the dish with a bite out of its right edge: three overlapping round tooth marks, centred on the right
 * edge of the dish's opaque pixels at mid height (a cut-out), or on the image's right edge (a full photo).
 * Made once per image and bite size, so the drop shadow follows the bitten outline.
 */
const bitten = new Map<string, { src: CanvasImageSource; out: OffscreenCanvas }>();
export function bittenImage(
  bmp: CanvasImageSource & { width: number; height: number },
  key: string,
  bite: number,
) {
  const k = `${key}|${bite}`;
  const hit = bitten.get(k);
  if (hit && hit.src === bmp) return hit.out;
  const w = bmp.width,
    h = bmp.height;
  const out = new OffscreenCanvas(w, h);
  const g = out.getContext('2d')!;
  g.drawImage(bmp, 0, 0);
  const r = bite * w,
    d = r * 1.15;
  // The dish's right edge on each tooth mark's row (an overhanging leaf elsewhere does not count).
  const rightEdge = (y: number) => {
    const row = g.getImageData(0, Math.min(h - 1, Math.max(0, Math.round(y))), w, 1).data;
    for (let x = w - 1; x >= 0; x--) if (row[x * 4 + 3]! > 128) return x;
    return w - 1;
  };
  const marks = [-d, 0, d].map((dy) => ({ y: h / 2 + dy, edge: rightEdge(h / 2 + dy) }));
  g.globalCompositeOperation = 'destination-out';
  for (const m of marks) {
    g.beginPath();
    g.arc(m.edge + r * 0.1, m.y, r, 0, Math.PI * 2);
    g.fill();
  }
  bitten.set(k, { src: bmp, out });
  return out;
}

/** Image slot: fills (frame background), the image clipped to the frame, then strokes (frame border). */
export function drawImage(g: CanvasRenderingContext2D, n: ImageNode, path: Path2D | null, doc: Doc | null) {
  if (!path) return;
  // An empty logo slot draws nothing at all (no backdrop): the restaurant may not have a logo.
  if (n.role === 'logo' && !n.assetId) return;
  for (const f of n.appearance.fills) {
    const st = paintStyle(g, f.paint, doc);
    if (!st) continue;
    g.save();
    g.globalAlpha *= f.opacity;
    g.globalCompositeOperation = composite(f.blend);
    g.fillStyle = st;
    g.fill(path);
    g.restore();
  }
  const bmp = n.assetId ? getBitmap(n.assetId) : null;
  g.save();
  g.clip(path);
  if (bmp) {
    const r = imageDrawRect(n);
    g.imageSmoothingQuality = 'high';
    // M20: a drop shadow on a photo slot follows the image's own alpha (a floating cut-out dish casts a soft
    // natural shadow). Canvas shadow sizes are in device pixels, so they scale with the current transform.
    const sh = n.appearance.effects.find((e) => e.kind === 'dropShadow' && e.enabled);
    if (sh) {
      const m = g.getTransform();
      const k = Math.sqrt(Math.abs(m.a * m.d - m.b * m.c)) || 1;
      g.shadowColor = cssColor(sh.params.color as ColorRef, doc);
      g.shadowBlur = Number(sh.params.blur ?? 0) * k;
      g.shadowOffsetX = Number(sh.params.x ?? 0) * k;
      g.shadowOffsetY = Number(sh.params.y ?? 0) * k;
    }
    if (n.flipX) {
      // M21b: mirrored inside its frame.
      g.translate(n.frame.w, 0);
      g.scale(-1, 1);
    }
    g.drawImage(n.bite ? bittenImage(bmp, n.assetId!, n.bite) : bmp, r.x, r.y, r.w, r.h);
  } else if (n.role !== 'logo') placeholder(g, n);
  g.restore();
  for (const s of n.appearance.strokes) {
    const st = paintStyle(g, s.paint, doc);
    if (!st || s.width <= 0) continue;
    g.save();
    g.globalAlpha *= s.opacity;
    g.strokeStyle = st;
    applyStroke(g, s);
    g.stroke(path);
    g.restore();
  }
}
